import React, { useEffect, useRef, useState } from 'react';
import { Button, Pagination, Popconfirm, Select, Table, Typography } from 'antd';
import moment from 'moment-timezone';

import TitleSection from '../../components/title-section/TitleSection';
import Input from '../../components/Input/Input';
import MaskedDatePicker from '../../components/masked-date-picker/masked-date-picker';
import Notification, { NOTIFICATION_TYPE } from '../../components/notification/Notification';
import handleError from '../../utils/handleError';
import { formatDateUtc } from '../../utils/moment.functions';
import { listSalesSFDiagnostic, resendSalesSFDiagnostic } from '../../utils/webApi';
import {
    DiagnosticCategory,
    DiagnosticSituation,
    IDiagnosticItem,
    IDiagnosticResendBody,
} from '../../models/sales-sf-diagnostic.models';
import CustomerEdit from './customer-edit/CustomerEdit';
import { CategoryTag, ReasonCell, SituationTag, formatToBRL, parseReason } from './diagnostic.helpers';

import './SalesSFDiagnostic.scss';

const { Option } = Select;
const { Text } = Typography;

interface IFilters {
    email: string,
    id: string,
    situation: DiagnosticSituation | '',
    category: DiagnosticCategory | '',
    from: string,
    to: string,
}

interface IQueryState {
    filters: IFilters,
    page: number,
    pageSize: number,
}

const initialFilters = (): IFilters => ({
    email: '',
    id: '',
    situation: '',
    category: '',
    from: moment().subtract(3, 'months').startOf('day').toISOString(),
    to: moment().endOf('day').toISOString(),
});

const toUtcParam = (date: string): string | undefined =>
    date ? moment(date).utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : undefined;

const SalesSFDiagnostic = () => {
    // Filtros em edição (formulário) x filtros aplicados (query): a paginação
    // sempre usa os filtros aplicados, nunca o que está digitado e não pesquisado.
    const [form, setForm] = useState<IFilters>(initialFilters);
    const [query, setQuery] = useState<IQueryState>(() => ({ filters: initialFilters(), page: 1, pageSize: 10 }));
    const [items, setItems] = useState<IDiagnosticItem[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [resendingKey, setResendingKey] = useState<string>();
    const [selectedItem, setSelectedItem] = useState<IDiagnosticItem>();
    const requestId = useRef(0);

    useEffect(() => {
        const currentRequest = ++requestId.current;
        const { filters, page, pageSize } = query;

        setIsLoading(true);
        listSalesSFDiagnostic({
            email: filters.email.trim() || undefined,
            id: filters.id.trim() || undefined,
            situation: filters.situation || undefined,
            category: filters.category || undefined,
            from: toUtcParam(filters.from),
            to: toUtcParam(filters.to),
            page,
            pageSize,
        })
            .then(response => {
                if (currentRequest !== requestId.current) return;
                setItems((response && response.results) || []);
                setTotal((response && response.total) || 0);
            })
            .catch(error => {
                if (currentRequest !== requestId.current) return;
                handleError(error);
            })
            .finally(() => {
                if (currentRequest === requestId.current) setIsLoading(false);
            });
    }, [query]);

    const search = (filters: IFilters) => {
        setQuery(prev => ({ ...prev, filters: { ...filters }, page: 1 }));
    };

    const reload = () => {
        setQuery(prev => ({ ...prev }));
    };

    const changeForm = (changes: Partial<IFilters>) => {
        setForm(prev => ({ ...prev, ...changes }));
    };

    const handleResend = async (item: IDiagnosticItem) => {
        // Sem orderId, o backend tenta o checkout Assine e, se a compra não nasceu lá
        // (lançada no admin, meio antigo), remonta a partir do pagamento no SCIS —
        // por isso userId/paymentId vão junto.
        const body: IDiagnosticResendBody = item.orderId
            ? { orderId: item.orderId }
            : { transactionId: item.transactionId, userId: item.userId, paymentId: item.paymentId };

        try {
            setResendingKey(item.key);
            const response = await resendSalesSFDiagnostic(body);
            const status = response && response.status;

            if (status === 'CREATED') {
                Notification(NOTIFICATION_TYPE.SUCCESS, 'Sucesso', 'Pedido criado no Salesforce com sucesso.');
            } else if (status === 'QUEUED') {
                Notification(NOTIFICATION_TYPE.INFO, 'Pedido enviado', 'Pedido enviado para a fila; atualize em instantes.');
            } else if (status === 'PENDING') {
                Notification(
                    NOTIFICATION_TYPE.WARNING,
                    'Pedido continua pendente',
                    (response && response.error) ? parseReason(response.error).summary : 'O Salesforce recusou o pedido novamente.'
                );
            } else {
                Notification(NOTIFICATION_TYPE.INFO, 'Reenvio concluído', `Status retornado: ${status || 'desconhecido'}`);
            }
            reload();
        } catch (error) {
            handleError(error);
        } finally {
            setResendingKey(undefined);
        }
    };

    const columns = [
        {
            title: 'Situação',
            dataIndex: 'situation',
            key: 'situation',
            render: (situation: string) => <SituationTag situation={situation} />,
        },
        {
            title: 'Causa',
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => <CategoryTag category={category} />,
        },
        {
            title: 'Motivo',
            dataIndex: 'reason',
            key: 'reason',
            className: 'col-reason',
            render: (reason: string) => <ReasonCell reason={reason} />,
        },
        {
            title: 'E-mail',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Produto',
            dataIndex: 'product',
            key: 'product',
        },
        {
            title: 'Assinatura',
            dataIndex: 'subscription',
            key: 'subscription',
        },
        {
            title: 'Meio de pagamento',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
        },
        {
            title: 'Valor',
            dataIndex: 'value',
            key: 'value',
            render: (value: number) => formatToBRL(value),
        },
        {
            title: 'Data de pagamento',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            render: (paymentDate: string) => paymentDate ? formatDateUtc(paymentDate, 'DD/MM/YYYY HH:mm') : '-',
        },
        {
            title: 'Ações',
            key: 'actions',
            render: (_: any, record: IDiagnosticItem) => {
                const isResending = resendingKey === record.key;

                return (
                    <span className="diagnostic-actions">
                        <Button size="small" icon="profile" onClick={() => setSelectedItem(record)}>
                            Detalhes
                        </Button>
                        <Popconfirm
                            title="Deseja reenviar esta venda ao Salesforce?"
                            okText="Sim"
                            cancelText="Não"
                            placement="left"
                            disabled={!!resendingKey}
                            onConfirm={() => handleResend(record)}
                        >
                            <Button
                                size="small"
                                type="primary"
                                icon="sync"
                                loading={isResending}
                                disabled={!!resendingKey && !isResending}
                            >
                                Reenviar
                            </Button>
                        </Popconfirm>
                    </span>
                );
            },
        },
    ];

    return (
        <section className="sales-sf-diagnostic">
            <TitleSection title="Diagnóstico Salesforce" />

            <form
                className="diagnostic-filter"
                onSubmit={e => {
                    e.preventDefault();
                    search(form);
                }}
            >
                <div className="input-filter">
                    <label>E-mail</label>
                    <Input
                        className="input-email"
                        placeholder="E-mail"
                        value={form.email}
                        onChange={({ target }) => changeForm({ email: target.value })}
                    />
                </div>

                <div className="input-filter">
                    <label>ID</label>
                    <Input
                        className="input-id"
                        placeholder="ID de pagamento, transação ou pedido"
                        value={form.id}
                        onChange={({ target }) => changeForm({ id: target.value })}
                    />
                </div>

                <div className="input-filter">
                    <label>Situação</label>
                    <Select
                        className="select-filter"
                        value={form.situation}
                        onChange={(situation: DiagnosticSituation | '') => changeForm({ situation })}
                    >
                        <Option value="">Todos</Option>
                        <Option value="PENDING">Pendente (PENDING)</Option>
                        <Option value="NAO_INTEGRADO">Não integrado</Option>
                    </Select>
                </div>

                <div className="input-filter">
                    <label>Causa</label>
                    <Select
                        className="select-filter"
                        value={form.category}
                        onChange={(category: DiagnosticCategory | '') => changeForm({ category })}
                    >
                        <Option value="">Todas</Option>
                        <Option value="ERRO">Erro</Option>
                    </Select>
                </div>

                <div className="input-filter">
                    <label>Início</label>
                    <div className="diagnostic-date">
                        <MaskedDatePicker
                            onChange={(_: any, from: string) => changeForm({ from: from || '' })}
                            currentValue={form.from}
                            stringType="from_date"
                            classText="input-date"
                            placeholder="DD/MM/AAAA"
                            forcePeriodOfDay="START"
                            onClean={() => changeForm({ from: '' })}
                        />
                    </div>
                </div>

                <div className="input-filter">
                    <label>Fim</label>
                    <div className="diagnostic-date">
                        <MaskedDatePicker
                            onChange={(_: any, to: string) => changeForm({ to: to || '' })}
                            currentValue={form.to}
                            stringType="to_date"
                            classText="input-date"
                            placeholder="DD/MM/AAAA"
                            forcePeriodOfDay="END"
                            onClean={() => changeForm({ to: '' })}
                        />
                    </div>
                </div>

                <Button
                    htmlType="submit"
                    className="btn-search"
                    type="primary"
                    icon="search"
                    disabled={isLoading}
                >
                    Pesquisar
                </Button>
            </form>

            <Table
                loading={isLoading}
                columns={columns}
                className="table-sales-diagnostic"
                rowKey="key"
                dataSource={items}
                pagination={false}
                scroll={{ x: true }}
                locale={{ emptyText: 'Nenhuma venda pendente de integração encontrada' }}
            />

            <div className="diagnostic-total">
                <Text><b>{total}</b> vendas encontradas</Text>
            </div>
            <Pagination
                className="diagnostic-pagination"
                showSizeChanger
                current={query.page}
                pageSize={query.pageSize}
                total={total}
                pageSizeOptions={['10', '25', '50', '100']}
                onChange={page => setQuery(prev => ({ ...prev, page }))}
                onShowSizeChange={(_, pageSize) => setQuery(prev => ({ ...prev, pageSize, page: 1 }))}
            />

            {selectedItem && (
                <CustomerEdit
                    item={selectedItem}
                    onClose={() => setSelectedItem(undefined)}
                    onSaved={() => {
                        setSelectedItem(undefined);
                        reload();
                    }}
                />
            )}
        </section>
    );
};

export default SalesSFDiagnostic;
